import type { Resolve } from '@doxyme/types';
import {
  useEffect,
  useState,
  type KeyboardEvent,
  type CSSProperties,
  type JSX,
} from 'react';
import { Popover, type PopoverProps } from '../popover';
import { useContextMenuStyles } from './contextMenu.styles';

export type ContextMenuProps = Resolve<
  Pick<
    PopoverProps,
    | 'children'
    | 'anchorElement'
    | 'openerElement'
    | 'placement'
    | 'isOpen'
    | 'onClose'
    | 'offset'
    | 'skidding'
    | 'flipPadding'
    | 'className'
    | 'preventScrollOnFocus'
    | 'disablePortal'
  > & {
    containerCss?: CSSProperties;
    /**
     * Disable the scale up animation for the context menu.
     * Needed for the context menu to be used in a call control bar.
     */
    disableAnimation?: boolean;
  }
>;

const MENU_OUT_DURATION_MS = 140;

export const ContextMenu = ({
  children,
  className,
  disableAnimation,
  ...rest
}: ContextMenuProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState(rest.isOpen ?? false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const isExternallyOpen = rest.isOpen ?? false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (isExternallyOpen) {
      setIsVisible(true);
      setIsClosing(false);
    } else if (isVisible) {
      if (disableAnimation) {
        setIsVisible(false);
        setIsClosing(false);
      } else {
        setIsClosing(true);

        timeoutId = setTimeout(() => {
          setIsVisible(false);
          setIsClosing(false);
        }, MENU_OUT_DURATION_MS);
      }
    }

    return () => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [rest.isOpen, isVisible, disableAnimation]);

  const styles = useContextMenuStyles({
    disableAnimation,
    isOpen: !isClosing,
  });

  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      rest.onClose?.();
    }
  }

  return (
    <Popover {...rest} isOpen={isVisible}>
      <div
        role='menu'
        tabIndex={-1}
        className={className}
        css={styles.container}
        onClick={() => rest.onClose?.()}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    </Popover>
  );
};
